import { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Trash2, UploadCloud } from "lucide-react";
import Notification from "../../components/UI/Notification";
import DeleteButton from "../../components/UI/AlertDialog";

const FILE_TYPES = [
  "Resumes",
  "OfferLetters",
  "JoiningLetters",
  "SalarySlips",
  "ExperienceLetters",
  "RelievingLetters",
  "CompanyPolicies"
];

const ALLOWED_FORMATS = [".csv", ".xlsx", ".pdf", ".docx"];

const FileManager = ({ token }) => {
  const [files, setFiles] = useState([]);
  const [fileType, setFileType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] =  useState(false);
 
  useEffect(() => {
    fetchFiles(); // Fetch files on mount
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true)
      const response = await fetch("https://hrmsnew.prepstripe.com/api/files", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      const data = await response.json();
      // console.log("Fetched data:", data); // Debugging
  
      if (data.success && Array.isArray(data.files)) {
        setLoading(false)
        setFiles(data.files); // Set only the array of files
      } else {
        setLoading(false)
        console.error("Unexpected API response format:", data);
        setError("Unexpected response format");
        // setNotification("Unexpected response format");
        setNotification({
          message: "Unexpected response format",
          type: "error",
        });
      }
    } catch (error) {
      setLoading(false)
      console.error("Error fetching files:", error);
      setError("Failed to load files");
      // setNotification("Failed to load files");
      setNotification({
        message: "Failed to load files",
        type: "error",
      });
    }
  };
  
  const onDrop = async (acceptedFiles) => {
    setError("");

    if (!fileType) {
      setError("Please select a file type before uploading.");
      // setNotification("Please select a file type before uploading.");
      setNotification({
        message: "Please select a file type before uploading.",
        type: "error",
      });
      return;
    }

    const filteredFiles = acceptedFiles.filter((file) =>
      ALLOWED_FORMATS.some((ext) => file.name.toLowerCase().endsWith(ext))
    );

    if (filteredFiles.length !== acceptedFiles.length) {
      setError("Only CSV, XLSX, PDF, and DOCX files are allowed.");
      // setNotification("Only CSV, XLSX, PDF, and DOCX files are allowed.");
      setNotification({
        message: "Only CSV, XLSX, PDF, and DOCX files are allowed.",
        type: "error",
      });
      return;
    }

    const newFiles = filteredFiles.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: (file.size / 1024).toFixed(2) + " KB",
      file,
      status: "Uploading...",
    }));

    setFiles((prev) => [...prev, ...newFiles]);
    await uploadFiles(newFiles);
  };

  const uploadFiles = async (filesToUpload) => {
    setUploading(true);
    const formData = new FormData();

    filesToUpload.forEach((file) => {
      formData.append("file", file.file);
    });

    formData.append("file_type", fileType);

    try {
      const response = await fetch("https://hrmsnew.prepstripe.com/api/files/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) throw setNotification({
                message: "Upload failed. Please try again.",
                type: "error",
            });

      if (response.ok) {
        setFiles([])
        setFileType("")
        // setNotification("File uploaded successfully.");
        setNotification({
          message: "File uploaded successfully.",
          type: "success",
        });
      }
      
      setFiles((prevFiles) =>
        prevFiles.map((file) =>
          filesToUpload.some((f) => f.id === file.id)
            ? { ...file, status: "Uploaded ✅" }
            : file
        )
      );

      fetchFiles(); // Refresh file list after upload
    } catch (error) {
      setFiles((prevFiles) =>
        prevFiles.map((file) =>
          filesToUpload.some((f) => f.id === file.id)
            ? { ...file, status: "Failed ❌" }
            : file
        )
      );
    }
    setUploading(false);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: ".csv, .xlsx, .pdf, .docx",
  });

  const deleteFile = async (fileName, fileType) => {
    // console.log("filename: ", fileName, "filetype: ", fileType)
    try {
      const response = await fetch("https://hrmsnew.prepstripe.com/api/files", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ file_name: fileName, file_type: fileType }),
      });
      if (!response.ok) throw new Error("Failed to delete file");
      setFiles(files.filter((file) => file.file_name !== fileName));
      // setNotification("File deleted successfully.");
      setNotification({
        message: "File deleted successfully.",
        type: "success",
      });
    } catch (error) {
      setError("Failed to delete file");
      // setNotification("Failed to delete file");
      setNotification({
        message: "Failed to delete file",
        type: "error",
      });
    }
  };
  

  return (
    <div className=" flex flex-col gap-6 px-4 font-poppins">
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Upload Section */}
      <div className="py-2 bg-white dark:bg-gray-900 rounded-xl w-full">
        <label className="block mb-2 dark:text-white text-sm font-medium text-gray-700">
          Select File Type:
        </label>
        <select
          value={fileType}
          onChange={(e) => setFileType(e.target.value)}
          className="w-full p-2 border dark:text-white dark:bg-black dark:border-gray-700 border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
        >
          <option value="">-- Select File Type --</option>
          {FILE_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className="border-2 border-dashed dark:bg-black dark:border-gray-700 border-gray-300 p-4 sm:p-6 text-center cursor-pointer rounded-lg hover:bg-gray-100 mt-4 transition"
        >
          <input {...getInputProps()} />
          <UploadCloud className="mx-auto text-blue-500" size={36} />
          <p className="text-gray-500 mt-2 text-sm sm:text-base">
            Drag & Drop files here, or click to upload
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Allowed formats: CSV, XLSX, PDF, DOCX
          </p>
        </div>

        {uploading && <p className="text-blue-500 mt-2 text-sm ">Uploading...</p>}
      </div>

      {/* Uploaded Files List */}
      <div className="py-2 bg-white dark:bg-black rounded-xl w-full">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <span className="text-gray-500 dark:text-white text-sm">Loading files...</span>
          </div>
        ) :files.length > 0 ? (
          <div className="mt-2 sm:mt-4">

            {/* search filter */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search by File Type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 md:text-md dark:border-gray-700 dark:bg-gray-950 dark:focus:outline-none dark:text-white text-sm border border-gray-300 rounded-lg focus:ring focus:ring-blue-200"
              />
            </div>
            {/* search filter */}

            <h2 className="text-base sm:text-lg font-semibold mb-4 dark:text-white">Uploaded Files</h2>
            <ul className="space-y-3">
              {files
                .filter((file) =>
                  file.file_type?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((file, index) => (
                <li
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border rounded-lg dark:border-gray-700 dark:bg-gray-950 bg-gray-50 hover:bg-gray-100 transition"
                >
                  <div className="flex flex-col mb-2 sm:mb-0">
                    <span className="text-sm font-medium text-gray-700 dark:text-white truncate">
                      {file.file_name}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{file.file_type}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <a
                      href={file.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline hover:underline-offset-4"
                    >
                      View File
                    </a>
                    <DeleteButton
                      style="text-red-600 hover:text-red-800 hover:underline hover:underline-offset-4"
                      onDelete={() => deleteFile(file.file_name, file.file_type)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-gray-500 text-sm dark:text-white">No files uploaded yet.</p>
        )}
      </div>
    </div>

  );
};

export default FileManager;
